import React, { useState, useEffect, useLayoutEffect, useCallback } from 'react';
import styles from './Accordion.module.scss';

export const Accordion = (props) => {
  const {
    defaultExpanded,
    index,
    expanded,
    setExpandedIndex,
    children,
    onClick,
    className,
    title,
    categoryId,
    categoryName,
    categoriesList,
    ...rest
  } = props;
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [shouldScroll, setShouldScroll] = useState(false);
  const [error, setError] = useState(null);

  const handleClick = useCallback((event) => {
    event.preventDefault();
    onClick?.(event, !expanded);
  }, [onClick, expanded]);

  const titleSlug = useCallback(() => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }, [title]);

  const categorySlug = useCallback(() => {
    return categoryName
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }, [categoryName]);

  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (url.hash) {
        const urlHash = url.hash.substring(1);
        if (urlHash === titleSlug()) {
          setExpandedIndex(index);
          setShouldScroll(true);
        }
      }
    } catch (err) {
      setError('Invalid URL format');
      console.error('Error parsing URL:', err);
    }
  }, [window.location.href, setExpandedIndex, index, titleSlug]);

  useLayoutEffect(() => {
    if (shouldScroll) {
      try {
        const scrollElement = document.getElementById(titleSlug());
        if (scrollElement) {
          const headerOffset = 100;
          const elementPosition = scrollElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      } catch (err) {
        console.error('Error scrolling to element:', err);
      } finally {
        setShouldScroll(false);
      }
    }
  }, [shouldScroll, expanded, titleSlug]);

  const onCopy = useCallback(async (e, text, copyIndex) => {
    e.stopPropagation();
    try {
      const url = new URL(window.location.origin);
      url.hash = text;
      
      if (categoryName && categoryName.trim() !== '') {
        const slug = categoryName
          .toLowerCase()
          .trim()
          .replace(/[^\w\s-]/g, '')
          .replace(/[\s_-]+/g, '-')
          .replace(/^-+|-+$/g, '');
        url.searchParams.set('category', slug);
      }
      
      await navigator.clipboard.writeText(url.toString());
      setCopiedIndex(copyIndex);
      setTimeout(() => {
        setCopiedIndex(null);
      }, 2000);
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      setError('Failed to copy link');
    }
  }, [categoryName]);

  if (error) {
    console.error(error);
  }

  return (
    <div
      className={`${styles.accordion} ${className}`}
      onClick={handleClick}
      role="region"
      aria-expanded={expanded}
      aria-labelledby={`accordion-title-${index}`}
      {...rest}
      id={titleSlug()}
    >
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <p id={`accordion-title-${index}`}>{title}</p>
          <div 
            className={styles.tooltipLink}
            role="button"
            tabIndex={0}
            aria-label="Copy link to this question"
            onKeyPress={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                onCopy(e, titleSlug(), index);
              }
            }}
          >
            <img 
              src="/images/link_icon.svg" 
              alt="Copy link" 
              onClick={(e) => onCopy(e, titleSlug(), index)}
            />
            <span 
              className={styles.tooltipText}
              role="tooltip"
              aria-hidden={copiedIndex !== index}
            >
              <p>{copiedIndex === index ? 'Copied!' : 'Copy link'}</p>
              <img src="/images/link_icon.svg" alt="" />
            </span>
          </div>
        </div>
        <button
          aria-label={expanded ? 'Collapse question' : 'Expand question'}
          aria-expanded={expanded}
        >
          {expanded ? 
            <img src="/images/minusRoundIcon.svg" alt="Collapse" /> :
            <img src="/images/plusRoundIcon.svg" alt="Expand" />
          }
        </button>
      </div>
      <div
        className={`${styles.content} ${expanded ? styles.expanded : ''}`}
        role="region"
        aria-hidden={!expanded}
      >
        <p className={styles.text}>
          {children}
        </p>
      </div>
    </div>
  );
};
